import React, { useRef, useState } from "react";
import { Upload, Loader2, ImageIcon } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";

export default function ImageUpload({ label, value, onChange, hint, aspect = "aspect-video", altValue, onAltChange, altLabel = "Image alt text (for SEO & accessibility)", altHint = "Describe the image in a few words, e.g. \"Accountant reviewing figures on a laptop\"." }) {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.uploadImage(fd);
      onChange(res.url);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try a smaller image", variant: "destructive" });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <Label className="text-navy font-semibold">{label}</Label>}
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      <div className="mt-2 flex items-center gap-4">
        <div className={`w-32 ${aspect} rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0`}>
          {value ? <img loading="lazy" decoding="async" src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="text-slate-300" size={24} />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-full">
            {uploading ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Uploading...</> : <><Upload size={15} className="mr-1.5" /> {value ? "Replace" : "Upload"}</>}
          </Button>
          {value && <button type="button" onClick={() => onChange("")} className="text-xs text-red-500 hover:underline text-left">Remove</button>}
        </div>
      </div>
      {onAltChange && (
        <div className="mt-3">
          <Label className="text-navy font-semibold text-sm">{altLabel}</Label>
          {altHint && <p className="text-xs text-slate-400 mt-0.5">{altHint}</p>}
          <input
            type="text"
            value={altValue || ""}
            onChange={(e) => onAltChange(e.target.value)}
            data-testid="image-alt-input"
            placeholder="Describe this image…"
            className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40"
          />
        </div>
      )}
    </div>
  );
}
